import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, take } from 'rxjs/operators';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Client } from 'src/providers/client/client';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { MarketingChannelsService, ChannelTableRow, AddressListing } from 'src/shared/services/marketing-channels.service';
import { DirectBookingSitesService, WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';
import { ChannelDetailComponent } from 'src/pages/marketing/components/channel-detail/channel-detail.component';
import { WebsiteDomainSettingsComponent } from 'src/pages/marketing/components/website-domain-settings/website-domain-settings.component';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { ListingSetupService } from 'src/shared/services/listing-setup.service';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { IonicModule } from '@ionic/angular';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'channels-tab',
  templateUrl: './channels-tab.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChannelsTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedProperty: any = null;

  headers = ['Property', 'Platform', 'Listing ID', 'Status', 'last-column'];
  allRows: ChannelTableRow[] = [];
  rows: ChannelTableRow[] = [];
  listings: AddressListing[] = [];
  // "Tell Us About Your Listings" header button: shown while at least one address hasn't
  // completed the flow. Every channel already has its own row, so there's no Add Listing.
  showListingsSetup = false;
  loaded = false;
  errorMessage = '';
  showAboutThisPage = false;
  overlayTop: string;

  private addressResponse: any[] = [];
  private _listingsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('listingsHeader', { read: ElementRef }) set listingsHeader(el: ElementRef) {
    this._listingsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly channelsService = inject(MarketingChannelsService);
  private readonly directBookingSites = inject(DirectBookingSitesService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  readonly windowService = inject(WindowService);
  private readonly client = inject(Client);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly setupService = inject(ListingSetupService);
  private readonly demoAccount = inject(DemoAccountService);

  searchForm = this.fb.group({ search: [''] });

  // True while Tell Us About Your Listings owns the panel, so only its close refreshes here.
  private wizardOpen = false;

  private isOpeningDirectBookingSite = false;

  async ngOnInit() {
    this.searchForm.get('search')!.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.filterRows(value || '');
      this.cdr.markForCheck();
    });

    // pairwise-based, so it only fires on a real open→close transition (panelClosed()
    // replays the current value and would fire at subscribe time instead).
    this.rightSidePanelService.afterPanelClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (!this.wizardOpen) {return;}
      this.wizardOpen = false;
      this.refreshData();
    });

    // Mobile-only path: deleting from the detail route can't rely on the panel-close
    // hook above, so the detail page signals the removal directly.
    this.channelsService.listingsChanged$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.refreshData();
    });

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('listings');
    if (this.showAboutThisPage) {
      this.loadMockData();
      return;
    }

    try {
      this.addressResponse = await this.client.getAddresses();
      await this.loadChannels();
    } catch (err: any) {
      this.errorMessage = err.error?.message || err.message;
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedProperty'] && !changes['selectedProperty'].firstChange) {
      this.applyPropertyFilter();
    }
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  private loadMockData() {
    this.headers = ['Property', 'Platform', 'Listing ID', 'Status', 'last-column'];
    const mockProperties = [
      { name: '123 Main St', color: '#4A90D9' },
      { name: 'Luxury Villa', color: '#50C878' },
      { name: 'Mountain Retreat', color: '#E8702A' },
      { name: 'Beach Bungalow', color: '#9B59B6' },
      { name: 'Cozy Cottage', color: '#E74C3C' },
      { name: 'Desert Oasis', color: '#F1C40F' },
    ];
    // Raw slugs and statuses, so the preview gets the same badges, dots and
    // labels as the real table.
    const mockPlatforms = ['airbnb', 'vrbo', 'booking_com'];
    const mockStatuses = ['listed', 'listed', 'listed', 'paused', 'connecting'];
    const mockRows: ChannelTableRow[] = [];
    for (let i = 0; i < 15; i++) {
      const property = mockProperties[i % mockProperties.length];
      const platform = mockPlatforms[i % mockPlatforms.length];
      const status = mockStatuses[i % mockStatuses.length];
      const listingId = `${platform.substring(0, 3).toUpperCase()}-${100000 + Math.floor(Math.random() * 900000)}`;
      mockRows.push({
        id: i + 1,
        addressId: i + 1,
        listingData: {} as AddressListing,
        statusLabel: this.channelsService.formatStatus(platform, status),
        platformLabel: this.channelsService.formatPlatform(platform),
        'Property': {
          value: property.name,
          action: null,
          color: property.color,
          addressId: i + 1,
        },
        'Platform': {
          value: this.channelsService.platformCellHtml(platform),
          action: null,
        },
        'Listing ID': {
          value: listingId,
          action: null,
        },
        'Status': {
          value: this.channelsService.statusCellHtml(platform, status),
          action: null,
        },
        'last-column': {
          value: i + 1,
          action: null,
          hasChevron: true,
        },
      });
    }
    this.allRows = mockRows;
    this.rows = [...mockRows];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._listingsHeader) {return;}
      const top = this._listingsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('listings');
    this.rows = [];
    this.allRows = [];
    this.cdr.markForCheck();
  }

  async loadChannels() {
    this.listings = await this.channelsService.fetchListings(this.addressResponse);
    // Every address always shows Airbnb / VRBO / Booking.com: a channel with no backend
    // listing becomes a "Needs setup" row (or "TIDY is creating your listing" once the client
    // said they don't have one). The shared demo account keeps its curated rows as-is.
    if (!this.demoAccount.isDemoSession()) {
      const state = await this.setupService.getSetupState(this.addressResponse.map(a => a.id));
      this.listings = this.setupService.withPlaceholderRows(this.addressResponse, this.listings, state);
      this.showListingsSetup = this.addressResponse.some(a => !state.get(a.id)?.listingsStepCompleted);
    }
    this.allRows = this.channelsService.buildRows(this.listings, this.addressResponse);
    this.applyPropertyFilter();
  }

  private applyPropertyFilter() {
    const propertyId = this.selectedProperty?.id || null;
    const propertyFiltered = this.channelsService.filterByProperty(this.allRows, propertyId);
    this.rows = [...propertyFiltered];
    this.cdr.markForCheck();
  }

  private filterRows(searchText: string): void {
    const propertyId = this.selectedProperty?.id || null;
    const propertyFiltered = this.channelsService.filterByProperty(this.allRows, propertyId);

    if (!searchText) {
      this.rows = [...propertyFiltered];
      return;
    }
    const lower = searchText.toLowerCase();
    // Match Status and Platform on their plain labels — their cell values carry
    // the dot/badge markup.
    this.rows = propertyFiltered.filter(row =>
      row.platformLabel?.toLowerCase().includes(lower) ||
      row['Property']?.value?.toLowerCase().includes(lower) ||
      row['Listing ID']?.value?.toLowerCase().includes(lower) ||
      row.statusLabel?.toLowerCase().includes(lower)
    );
  }

  clearSearch(): void {
    this.searchForm.get('search')?.setValue('');
    this.applyPropertyFilter();
  }

  sortChanged(sort: any): void {
    const multiplier = sort.direction === 'asc' ? 1 : -1;
    // Status and Platform sort on their plain labels; their cell values start
    // with dot/badge markup, which would drive the ordering instead of the word.
    const sortValue = (row: ChannelTableRow) => {
      if (sort.active === 'Status') {return row.statusLabel;}
      if (sort.active === 'Platform') {return row.platformLabel;}
      return row[sort.active]?.value;
    };
    this.rows = [...this.rows].sort((a, b) => {
      const aValue = sortValue(a);
      const bValue = sortValue(b);
      if (aValue !== undefined && bValue !== undefined) {
        if (aValue < bValue) {return -1 * multiplier;}
        if (aValue > bValue) {return 1 * multiplier;}
      }
      return 0;
    });
    this.cdr.markForCheck();
  }

  onTableColumnClick(event: { column: string; element: any }) {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) {return;}
    const listing = (event?.element?.listingData ?? (event as any)?.listingData) as AddressListing;
    if (!listing) {return;}
    // A synthesized row has no listing behind it — ask the one question for this
    // address × channel instead of opening a detail page.
    if (listing.isPlaceholder) {
      this.openListingsSetup({ addressId: listing.address_id, platform: listing.platform });
      return;
    }
    // A tidy row is the same property as its Direct Booking sites row, so both
    // doors have to open the same page.
    if (listing.platform === 'tidy') {
      void this.openDirectBookingSite(listing);
      return;
    }
    this.openChannelDetail(listing);
  }

  private async openDirectBookingSite(listing: AddressListing) {
    // fetchListings attaches the address it queried; addressResponse is the
    // fallback for rows that arrived without one.
    const address = listing.address || this.addressResponse.find(a => a.id === listing.address_id);
    // Re-entrancy guard: building the config awaits two reads, which is long
    // enough for a second click to land and open a duplicate panel.
    if (!address || this.isOpeningDirectBookingSite) {return;}
    this.isOpeningDirectBookingSite = true;
    let config: WebsiteConfig;
    try {
      config = await this.directBookingSites.buildConfig(address);
    } catch {
      // Never strand the click — the site and editor URLs come from the address
      // alone, and the detail page reads publish state itself on open.
      config = this.directBookingSites.buildLocalConfig(address);
    } finally {
      this.isOpeningDirectBookingSite = false;
    }

    // No setDialogPageTitle here: WebsiteDomainSettingsComponent sets its own
    // title and address subtitle, exactly as when opened from Direct Booking.
    const params = { websiteConfig: config };
    this.storage.save('dialog-params', params);

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(WebsiteDomainSettingsComponent);
    } else {
      this.navCtrl.navigateForward('website-domain-settings', params);
    }

    // Toggling publish on the detail page changes this row's status.
    this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
      this.refreshData();
    });
  }

  private openChannelDetail(listing: AddressListing) {
    if (this.showAboutThisPage) {return;}
    const params = { addressId: listing.address_id, listing };

    this.storage.save('dialog-right-side-open', true);
    this.storage.save('dialog-params', params);
    this.rightSidePanelService.setDialogPageTitle('Listing Details');

    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.openRightSidePanel(ChannelDetailComponent);
    } else {
      this.navCtrl.navigateForward('revenue/listing-detail');
    }

    this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
      this.refreshData();
    });
  }

  // The header button runs Tell Us About Your Listings — every property × channel on one
  // page — the same flow every listings action item opens.
  goToListingsSetup = () => {
    this.openListingsSetup({ addressId: this.selectedProperty?.id || this.addressResponse[0]?.id });
  };

  // Full flow ({ addressId }) or the single-question version ({ addressId, platform }).
  // navigateTo → swaps within the desktop panel, full-page route on mobile. The close
  // callback is the afterPanelClosed() subscription set up in ngOnInit: navigateTo is
  // async, so panelClosed() here would fire before the panel even opens.
  private openListingsSetup(params: { addressId?: number; platform?: string }): void {
    if (this.demoBlock.block()) {return;}
    this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
    this.wizardOpen = true;
    this.rightSidePanelService.navigateTo(
      'listings-setup',
      { ...params, returnRoute: 'listings' },
      ListingsSetupComponent
    );
  }

  private async refreshData() {
    try {
      // Re-read addresses first: the flow can add a property, and loadChannels only
      // sees listings for the addresses it's given.
      this.addressResponse = await this.client.getAddresses(true);
      await this.loadChannels();
      this.cdr.markForCheck();
    } catch {
      // Non-fatal
    }
  }
}
